import { parseIsoDay } from './dates';

/**
 * Calendário de calor (WP-04), no formato do PriceCalendar do DS: nível 1 = mais alto, 5 = mais baixo, por quintil
 * dentro da mesma rota e janela. Dia sem preço fica "sem dado" (price null), nunca vira nível 1.
 */
const MONTHS_LONG = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

export type HeatLevel = 1 | 2 | 3 | 4 | 5;

/**
 * Nível de cada preço pela posição entre os valores distintos do conjunto, em cinco faixas (quintis): o mais
 * barato é 5 e o mais caro é 1. Empates ficam no mesmo nível; um preço só é 3.
 */
export function heatLevels(prices: readonly number[]): HeatLevel[] {
  const distinct = [...new Set(prices)].sort((a, b) => a - b);
  if (distinct.length === 1) return prices.map(() => 3 as HeatLevel);
  return prices.map((p) => {
    const pos = distinct.indexOf(p) / (distinct.length - 1);
    return (5 - Math.round(pos * 4)) as HeatLevel;
  });
}

export interface CalendarDay {
  day: number;
  price: number | null;
  /** Ausente quando não há preço (o DS mostra "Sem dado"). */
  level?: HeatLevel;
  selected: boolean;
}

export interface CalendarMonth {
  /** AAAA-MM */
  key: string;
  /** "Maio de 2027" */
  label: string;
  /** 0 = domingo */
  startWeekday: number;
  days: CalendarDay[];
}

/** Um mês por mês tocado pelas datas com preço, com os níveis calculados sobre todas elas juntas. */
export function priceCalendar(
  entries: readonly { date: string; price: number }[],
  selected: string,
): CalendarMonth[] {
  const levels = heatLevels(entries.map((e) => e.price));
  const byDate = new Map(entries.map((e, i) => [e.date, { price: e.price, level: levels[i]! }]));
  const keys = [...new Set([...entries.map((e) => e.date.slice(0, 7)), selected.slice(0, 7)])].sort();
  return keys.map((key) => {
    const [y, m] = key.split('-').map(Number) as [number, number];
    const total = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const days: CalendarDay[] = [];
    for (let d = 1; d <= total; d++) {
      const date = `${key}-${String(d).padStart(2, '0')}`;
      const hit = byDate.get(date);
      days.push(
        hit
          ? { day: d, price: hit.price, level: hit.level, selected: date === selected }
          : { day: d, price: null, selected: date === selected },
      );
    }
    return {
      key,
      label: `${MONTHS_LONG[m - 1]} de ${y}`,
      startWeekday: parseIsoDay(`${key}-01`).getUTCDay(),
      days,
    };
  });
}
